import { useEffect, useMemo, useState } from "react";
import { useConnectStore } from "../../store/Connect";
import { Logger } from "@/lib/logger/Logger";
import { useRealtimeStore } from "@/lib/store/Realtime";
import { ChannelComponent } from "../channel/channel.component";
import { useNavigate } from "react-router-dom";
import { usePostHog } from "posthog-js/react";
import { isDentconnectUser } from "@/lib/utils/is-app-user";
import { ClientManagementDialog } from "../../pages-via-dialog/client-management/client-management.dialog";
import { ClientList } from "../list-components/client-list/client-list.component";
import { Button } from "../ui/button";
import { showNotification } from "../../store/Central/selectors";
import { AcceptJobRequestResult } from "../../store/Jobs/job.store";

export const ChatComponent: React.FC<{
	// We allow null because we let the channel component cover the case where there is no channel (e.g. job chat where job has no channel yet)
	channelId: string | null;

	organizationId: string;
	orgsLookup?: Record<string, { name: string }>;
	useSubtitleAsTitle?: boolean;
	onShareFile: (fileId: string) => Promise<void>;
	onUnshareFile: (fileId: string) => Promise<void>;
	onAcceptJobRequest?: (
		channelId: string | null
	) => Promise<AcceptJobRequestResult>;
	onDenyJobRequest?: (channelId: string | null) => Promise<boolean>;
	allowGoTo?: boolean;
	updateJobFiles?: () => void;

	// For empty channels
	jobId?: number;
	vendorOrgId?: string;
	customerOrgId?: string;

	// Client organization IDs, for knowing whether the channel customer has a client connected to them
	clientOrganizationIds?: string[];
	checkForClientConnectionToCustomer?: boolean; // For the edge case where a new lab is added via dentconnect and doesn't have clients yet.
}> = ({
	organizationId,
	onShareFile,
	onUnshareFile,
	onAcceptJobRequest,
	onDenyJobRequest,
	channelId,
	allowGoTo = true,
	updateJobFiles,
	jobId,
	vendorOrgId,
	customerOrgId,
	clientOrganizationIds,
	checkForClientConnectionToCustomer = false,
}) => {
	const navigate = useNavigate();
	const posthog = usePostHog();
	const { brandsByOrgId, fetchBrands } = useConnectStore((state) => ({
		brandsByOrgId: state.brandsByOrgId,
		fetchBrands: state.fetchBrands,
	}));
	const {
		channel,
		messages,
		messagesLoading,
		changeChannel,
		updateChannelReadState,
		profilesForChat,
	} = useRealtimeStore((state) => ({
		channel: state.channel,
		messages: state.messages,
		messagesLoading: state.messagesLoading,
		changeChannel: state.changeChannel,
		updateChannelReadState: state.updateChannelReadState,
		profilesForChat: state.profilesForChat,
	}));
	useEffect(() => {
		if (channelId) {
			changeChannel(channelId);
		}
	}, [channelId]);

	useEffect(() => {
		if (!brandsByOrgId || Object.keys(brandsByOrgId).length === 0) {
			fetchBrands(organizationId);
		}
		Logger.log("Brands by org id", brandsByOrgId);
	}, [organizationId]);

	// Handle window focus to update read state
	useEffect(() => {
		if (!channel || !channel.id) {
			return;
		}

		// For first load, update read state if window is focused
		if (document.hasFocus()) {
			updateChannelReadState(channel.id);
		}

		const handleFocus = () => {
			// When window regains focus, update read state for current channel
			if (channel && channel.id) {
				updateChannelReadState(channel.id);
			}
		};

		window.addEventListener("focus", handleFocus);

		return () => {
			window.removeEventListener("focus", handleFocus);
		};
	}, [channel, updateChannelReadState]);

	const messagesForChannel = useMemo(() => {
		if (!channel || !channel.id) {
			return [];
		}
		return messages[channel.id as string] || [];
	}, [messages, channel?.id]);

	const { connectVendeeAsClient } = useConnectStore((state) => ({
		connectVendeeAsClient: state.connectVendeeAsClient,
	}));

	const [clientSelectDialogOpen, setClientSelectDialogOpen] = useState(false);
	const [createNewClientOpen, setCreateNewClientOpen] = useState(false);

	const handleConnectCustomerAsClient = async (
		clientId: string,
		otherOrganizationId: string
	) => {
		if (otherOrganizationId) {
			const success = await connectVendeeAsClient(
				otherOrganizationId,
				clientId
			);
			if (success) {
				window.location.reload();
			}
		}
	};

	const weAreTheVendor = channel?.vendor_org_id === organizationId;

	const notConnectedYet = useMemo(() => {
		// If we should not check, we don't care
		if (!checkForClientConnectionToCustomer) {
			return false;
			// We are not the vendor, so it is not relevant whether we have a client for the other organization
		} else if (!weAreTheVendor) {
			return false;
			// The customer id is in our list of clients, so they are already connected
		} else if (
			clientOrganizationIds &&
			clientOrganizationIds.length > 0 &&
			clientOrganizationIds?.includes(channel?.customer_org_id ?? "")
		) {
			return false;
		} else {
			return true;
		}
	}, [
		checkForClientConnectionToCustomer,
		weAreTheVendor,
		clientOrganizationIds,
		channel?.customer_org_id,
	]);

	return (
		<div className="flex h-full min-h-0 flex-col">
			{!isDentconnectUser(import.meta.env) && notConnectedYet && (
				<>
					<div className="bg-blue-50 p-4 rounded-lg flex items-center gap-4 m-2 flex-shrink-0">
						<div className="flex-1">
							<p className="text-sm text-gray-700">
								Der Absender dieses Chats ist noch mit keinem
								Ihrer Kunden verbunden.
							</p>
						</div>
						<Button
							onClick={() => {
								setClientSelectDialogOpen(true);
							}}
						>
							Kunde verbinden
						</Button>
					</div>
					<ClientList
						open={clientSelectDialogOpen}
						onClick={(client) => {
							setClientSelectDialogOpen(false);
							if (!client.id) {
								showNotification({
									message: "Kunde nicht gefunden",
									type: "error",
								});
								Logger.error(
									"Client ID not found when connecting customer as client"
								);
								return;
							}
							handleConnectCustomerAsClient(
								client.id,
								channel?.customer_org_id ?? ""
							);
						}}
						setOpen={setClientSelectDialogOpen}
						openForm={() => {
							setClientSelectDialogOpen(false);
							setCreateNewClientOpen(true);
						}}
					/>
					<ClientManagementDialog
						open={createNewClientOpen}
						onClose={(client) => {
							setCreateNewClientOpen(false);
							if (client) {
								handleConnectCustomerAsClient(
									client.id,
									channel?.customer_org_id ?? ""
								);
							}
						}}
					/>
				</>
			)}
			<ChannelComponent
				channel={channel}
				organizationId={organizationId}
				profileLookup={profilesForChat}
				messages={messagesForChannel}
				messagesLoading={messagesLoading}
				onShareFile={onShareFile}
				onUnshareFile={onUnshareFile}
				onAcceptJobRequest={onAcceptJobRequest}
				onDenyJobRequest={onDenyJobRequest}
				goTo={
					allowGoTo && channel?.job_id
						? () => {
								posthog.capture("job_navigated_from_chat", {
									role: weAreTheVendor
										? "vendor"
										: "customer",
									source_surface: "chats_page"
								});
								if (weAreTheVendor && channel.job_client_id) {
									navigate(
										`/client/${channel.job_client_id}/job/${channel.job_id}`
									);
								} else if (channel.vendor_org_id) {
									navigate(
										`/vendor/${channel.vendor_org_id}/job/${channel.job_id}`
									);
								}
							}
						: undefined
				}
				updateJobFiles={updateJobFiles}
				jobId={jobId}
				vendorOrgId={vendorOrgId}
				customerOrgId={customerOrgId}
			/>
		</div>
	);
};
